import { Tooltip } from './Tooltip';
import { Icon } from '@/lib/icons';
import { getTooltip, type TooltipEntry } from '@/lib/tooltips';
import { cn } from '@/lib/cn';

interface Props {
  /** Lookup-Key im Tooltip-Inventory (`<scope>.<feld>`). */
  helpKey: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  /** Sichtbarer aria-Label-Suffix; default: „Hilfe“. */
  label?: string;
}

/**
 * Kleines Hilfe-Icon, das aus dem zentralen Tooltip-Inventory speist.
 * Rendert nichts, wenn der Key unbekannt ist.
 */
export function HelpIcon({ helpKey, placement = 'top', className, label = 'Hilfe' }: Props) {
  const entry = getTooltip(helpKey);
  if (!entry) return null;

  return (
    <Tooltip content={<TooltipBody entry={entry} />} placement={placement} maxWidth={300}>
      <button
        type="button"
        tabIndex={0}
        aria-label={`${entry.title ?? label}: Erklärung anzeigen`}
        className={cn(
          'inline-flex h-4 w-4 items-center justify-center rounded-full',
          'text-ink-400 hover:text-ink-700 focus:text-ink-700',
          'focus:outline-none focus:ring-1 focus:ring-accent-500',
          'transition-colors',
          className,
        )}
      >
        <Icon name="HelpCircle" size={11} strokeWidth={2} aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

function TooltipBody({ entry }: { entry: TooltipEntry }) {
  return (
    <div className="space-y-1">
      {entry.title ? <p className="font-semibold text-paper">{entry.title}</p> : null}
      <p className="text-paper/90">{entry.body}</p>
      {entry.legalRef ? (
        <p className="pt-1 text-[10px] uppercase tracking-wide text-paper/60">{entry.legalRef}</p>
      ) : null}
    </div>
  );
}
